import { useWorkspaceText } from '../interface-copy';
import { useNamespaceCopy } from '@/i18n/ns';
import editorCopy from '@/i18n/ns/workspace-editors';
import {
  Component,
  lazy,
  Suspense,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { useOutsidePress } from '@/lib/use-outside-press';
import type { KnowledgeEditorProps, KnowledgeEditorSession } from './editor/knowledge-editor';
import type { PageLinkRange } from './editor/page-link';
import { loadKnowledgeEditor } from './editor/load';
import { useWorkspace } from '../context';
import { useReportUnsavedEdits } from '../edit-owner';
import { WorkspaceIcon } from '../icons';
import { LoadingState } from '../primitives';
import { announceKnowledgePagesChanged } from '../knowledge-agent-events';
import { errorCode, knowledgePagesApi, pagesErrorMessage } from './api';
import { PagePicker } from './page-picker';
import type { TreeIndex } from './tree';
import type { WriteResult } from './types';

const MAX_PAGE_BYTES = 262_144;

type EditorModule = { default: ComponentType<KnowledgeEditorProps> };
let loader: () => Promise<EditorModule> = loadKnowledgeEditor;
let LazyEditor = lazy(loader);

/** Tests replace the editor chunk (a stand-in, or a failing load). */
export function setKnowledgeEditorLoaderForTests(next: (() => Promise<EditorModule>) | null) {
  loader = next ?? loadKnowledgeEditor;
  LazyEditor = lazy(loader);
}

/**
 * The editor is a separate chunk. After a deployment a page opened earlier
 * cannot fetch it (its hashed file is gone), so a failed load offers a reload
 * instead of an error the person cannot act on.
 */
class EditorLoadBoundary extends Component<
  { children: ReactNode; onCancel: () => void },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return <EditorLoadFailure onCancel={this.props.onCancel} />;
  }
}

function EditorLoadFailure({ onCancel }: { onCancel: () => void }) {
  const { text: uiText } = useWorkspaceText();
  return (
    <div className="ws-page-banner ws-editor-failed" role="alert">
      <p>
        {uiText(
          'The editor could not be loaded. Poneva may have been updated since you opened this page. Reload to get the latest version; nothing on this page has changed.',
        )}
      </p>
      <div className="ws-actions">
        <button type="button" className="ws-button primary" onClick={() => location.reload()}>
          {uiText('Reload Poneva')}
        </button>
        <button type="button" className="ws-button" onClick={onCancel}>
          {uiText('Cancel')}
        </button>
      </div>
    </div>
  );
}

/** Re-render on every editor transaction, so the toolbar shows the current marks. */
function useEditorTick(session: KnowledgeEditorSession | null) {
  const [, tick] = useReducer((value: number) => value + 1, 0);
  useEffect(() => {
    if (!session) return;
    const editor = session.editor;
    editor.on('transaction', tick);
    return () => {
      editor.off('transaction', tick);
    };
  }, [session]);
}

function Toolbar({
  session,
  onPageLink,
}: {
  session: KnowledgeEditorSession | null;
  onPageLink: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  useEditorTick(session);
  const editor = session?.editor ?? null;
  const run = (command: (chain: ReturnType<NonNullable<typeof editor>['chain']>) => unknown) => {
    if (!editor) return;
    command(editor.chain().focus());
  };
  const block = !editor
    ? 'paragraph'
    : editor.isActive('heading', { level: 2 })
      ? 'h2'
      : editor.isActive('heading', { level: 3 })
        ? 'h3'
        : 'paragraph';
  const tool = (label: string, active: boolean, onClick: () => void, children: ReactNode) => (
    <button
      type="button"
      className="ws-editor-tool"
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={!editor}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
  return (
    <div className="ws-editor-toolbar" role="toolbar" aria-label={uiText('Formatting')}>
      <label className="ws-editor-block">
        <span className="ws-sr-only">{uiText('Text style')}</span>
        <select
          value={block}
          disabled={!editor}
          onChange={(event) => {
            const value = event.target.value;
            run((chain) =>
              (value === 'paragraph'
                ? chain.setParagraph()
                : chain.setHeading({ level: value === 'h2' ? 2 : 3 })
              ).run(),
            );
          }}
        >
          <option value="paragraph">{uiText('Text')}</option>
          <option value="h2">{uiText('Heading')}</option>
          <option value="h3">{uiText('Subheading')}</option>
        </select>
      </label>
      <span className="ws-editor-separator" aria-hidden="true" />
      {tool(
        'Bold',
        Boolean(editor?.isActive('bold')),
        () => run((c) => c.toggleBold().run()),
        <b>B</b>,
      )}
      {tool(
        'Italic',
        Boolean(editor?.isActive('italic')),
        () => run((c) => c.toggleItalic().run()),
        <i>I</i>,
      )}
      <span className="ws-editor-separator" aria-hidden="true" />
      {tool(
        uiText('Bulleted list'),
        Boolean(editor?.isActive('bulletList')),
        () => run((c) => c.toggleBulletList().run()),
        <span aria-hidden="true">•≡</span>,
      )}
      {tool(
        uiText('Numbered list'),
        Boolean(editor?.isActive('orderedList')),
        () => run((c) => c.toggleOrderedList().run()),
        <span aria-hidden="true">1≡</span>,
      )}
      {tool(
        'Table',
        Boolean(editor?.isActive('table')),
        () => run((c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()),
        <WorkspaceIcon name="table" size={15} />,
      )}
      <span className="ws-editor-separator" aria-hidden="true" />
      {tool(
        uiText('Web link'),
        Boolean(editor?.isActive('link')),
        () => {
          if (!editor) return;
          if (editor.isActive('link')) return run((c) => c.unsetLink().run());
          const href = window.prompt('Link address (https://…)');
          if (href?.trim()) run((c) => c.setLink({ href: href.trim() }).run());
        },
        <WorkspaceIcon name="source" size={15} />,
      )}
      <button
        type="button"
        className="ws-editor-tool ws-editor-tool-wide"
        disabled={!editor}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onPageLink}
      >
        {uiText('Link a page')}
      </button>
    </div>
  );
}

export type EditorTarget =
  | {
      kind: 'page';
      pageId: string;
      /** The text and revision the edit starts from; kept fixed while editing. */
      baseRevisionId: string | null;
      markdown: string;
      title: string;
      openSuggestions: number;
    }
  | { kind: 'new'; parentId: string | null; parentTitle: string; title: string };

interface PickerState {
  range: PageLinkRange;
  /** The text the range held when the picker opened; a link replaces only that. */
  expected: string;
  top: number;
  left: number;
  label: string | null;
}

/** The text of `range` in the editor now, or null when the range is gone. */
function rangeText(session: KnowledgeEditorSession, range: PageLinkRange) {
  const doc = session.editor.state.doc;
  try {
    return doc.textBetween(range.from, range.to, ' ');
  } catch {
    return null;
  }
}

/**
 * Edit a page (or write a new one) in the rich-text editor. A publisher's
 * save is the new revision; anyone else sends a suggestion with a reason.
 * The Markdown sent is the byte-stable splice of the editor's document
 * (editor/markdown-splice.ts), so untouched text stays exactly as it was.
 */
export function PageEditor({
  target,
  index,
  canPublish,
  onDone,
  onCancel,
}: {
  target: EditorTarget;
  index: TreeIndex | null;
  canPublish: boolean;
  onDone: (result: WriteResult) => void;
  onCancel: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  // The full Knowledge page owns its edits; docked beside a chat, the canvas does.
  const reportUnsaved = useReportUnsavedEdits();
  const [title, setTitle] = useState(target.title);
  const [note, setNote] = useState('');
  const [session, setSession] = useState<KnowledgeEditorSession | null>(null);
  const [edited, setEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [notice, setNotice] = useState<string[]>([]);
  const [picker, setPicker] = useState<PickerState | null>(null);
  const pickerRef = useRef<PickerState | null>(null);
  pickerRef.current = picker;
  // A press outside the picker (the title, the note, the toolbar) closes it, as the editor's focus does.
  const pickerPanel = useRef<HTMLDivElement | null>(null);
  useOutsidePress(Boolean(picker), pickerPanel, () => setPicker(null));
  const [creating, setCreating] = useState(false);
  const surface = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);
  const titleChanged = title.trim() !== target.title.trim();
  const dirty = edited || titleChanged || (target.kind === 'new' && Boolean(title.trim()));
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    reportUnsaved(dirty);
  }, [dirty]);
  useEffect(() => () => reportUnsaved(false), []);
  // Leaving the browser tab with unsaved text asks first.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  // Which edited blocks the save rewrites beyond the change itself, said quietly.
  useEffect(() => {
    if (!session) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = () => {
      setEdited(true);
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!mounted.current) return;
        const saved = session.save();
        setEdited(saved.changed);
        if (!saved.changed) return setNotice([]);
        const original = target.kind === 'page' ? target.markdown : '';
        const rewritten = session
          .normalizations()
          .filter((block) => !saved.markdown.includes(original.slice(block.start, block.end)));
        setNotice([
          ...(rewritten.length
            ? [
                'The formatting of the part you changed (a list or a table, for example) is saved in standard Markdown.',
              ]
            : []),
          ...(saved.exact
            ? []
            : [
                'Part of your edit has no exact Markdown form and may read slightly differently after saving.',
              ]),
        ]);
      }, 300);
    };
    session.editor.on('update', check);
    return () => {
      clearTimeout(timer);
      session.editor.off('update', check);
    };
  }, [session]);

  // Typing in the text or going back to it closes the picker: its range would
  // no longer be the brackets it opened on.
  useEffect(() => {
    if (!session || !picker) return;
    const editor = session.editor;
    const close = () => setPicker(null);
    editor.on('focus', close);
    editor.on('update', close);
    return () => {
      editor.off('focus', close);
      editor.off('update', close);
    };
  }, [session, Boolean(picker)]);

  const editor = useNamespaceCopy(editorCopy);
  const reasonLabel = canPublish ? editor.noteOptional : editor.whyThisChange;
  const pageId = target.kind === 'page' ? target.pageId : null;
  async function save() {
    if (!session || busy) return;
    const saved = session.save();
    const markdown = saved.markdown;
    const reason = note.trim();
    if (!title.trim()) return setError(uiText('A page needs a title.'));
    if (target.kind === 'page' && !saved.changed && !titleChanged)
      return setError(uiText('Nothing has changed yet.'));
    if (target.kind === 'page' && !markdown.trim())
      return setError(uiText('A page needs some text. To remove a page, archive it instead.'));
    if (target.kind === 'new' && !canPublish && !markdown.trim())
      return setError(uiText('A suggested new page needs some text.'));
    if (new TextEncoder().encode(markdown).length > MAX_PAGE_BYTES)
      return setError(uiText('This page is larger than 256 KB. Split it into smaller pages.'));
    if (!canPublish && !reason)
      return setError(uiText('Say in a few words why this change is right.'));
    setBusy(true);
    setError('');
    setConflict(false);
    try {
      const result =
        target.kind === 'page'
          ? await knowledgePagesApi.save(target.pageId, {
              baseRevisionId: target.baseRevisionId,
              markdown,
              ...(titleChanged ? { title: title.trim() } : {}),
              ...(canPublish ? { note: reason || undefined } : { reason }),
            })
          : await knowledgePagesApi.create({
              title: title.trim(),
              parentId: target.parentId,
              ...(markdown.trim() ? { markdown } : {}),
              ...(canPublish ? { note: reason || undefined } : { reason }),
            });
      if (!mounted.current) return;
      reportUnsaved(false);
      announceKnowledgePagesChanged();
      onDone(result);
    } catch (failure) {
      if (!mounted.current) return;
      if (errorCode(failure) === 'revision_conflict') {
        setConflict(true);
        // Re-read the page now, so "Open the latest version" shows it and the
        // next Edit starts from the current revision.
        announceKnowledgePagesChanged();
        setError(
          uiText(
            'This page changed while you were editing. Your text is still here: copy it, then open the latest version and make your change again.',
          ),
        );
      } else
        setError(
          pagesErrorMessage(failure, uiText('We could not save. Please try again.'), uiText.locale),
        );
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
  async function copyDraft() {
    if (!session) return;
    try {
      await navigator.clipboard.writeText(session.save().markdown);
      ws.notify(uiText('Your text is copied.'), null, 'success');
    } catch {
      ws.notify(
        uiText('Your text could not be copied. Select it in the editor and copy it.'),
        null,
        'error',
      );
    }
  }
  function cancel() {
    if (dirty && !window.confirm(editor.discardChangesConfirm)) return;
    reportUnsaved(false);
    onCancel();
  }
  function openPicker(range: PageLinkRange, label: string | null) {
    const editor = session?.editor;
    const box = surface.current?.getBoundingClientRect();
    let top = 0;
    let left = 0;
    try {
      const at = editor?.view.coordsAtPos(range.to);
      if (at && box) {
        top = at.bottom - box.top + 6;
        left = Math.max(0, Math.min(at.left - box.left, box.width - 320));
      }
    } catch {
      // Without layout (a hidden editor) the picker opens at the top.
    }
    setPicker({
      range,
      expected: session ? (rangeText(session, range) ?? '') : '',
      top,
      left,
      label,
    });
  }
  function insertLink(page: { id: string; title: string }) {
    const open = pickerRef.current;
    setPicker(null);
    if (!session || !open) return;
    // Only the text the picker opened on is replaced; if it changed, nothing is.
    if (rangeText(session, open.range) !== open.expected) return;
    session.editor
      .chain()
      .focus()
      .insertPageLink({ pageId: page.id, label: open.label ?? page.title, range: open.range })
      .run();
  }
  async function createAndLink(name: string) {
    if (!canPublish || creating) return;
    setCreating(true);
    try {
      const created = await knowledgePagesApi.create({
        title: name,
        parentId: pageId ?? (target.kind === 'new' ? target.parentId : null),
      });
      if (!mounted.current || !created.pageId) return;
      announceKnowledgePagesChanged();
      // The picker may have closed while the page was created: then no link.
      if (pickerRef.current) insertLink({ id: created.pageId, title: name });
      else ws.notify(uiText('The page “{value1}” is created.', { value1: name }), null, 'success');
    } catch (failure) {
      if (mounted.current)
        setError(
          pagesErrorMessage(failure, uiText('The new page could not be created.'), uiText.locale),
        );
    } finally {
      if (mounted.current) setCreating(false);
    }
  }
  const openSuggestions = target.kind === 'page' ? target.openSuggestions : 0;
  const createUnder =
    target.kind === 'page' ? target.title : target.kind === 'new' ? target.parentTitle : undefined;
  return (
    <form
      className="ws-page-editor"
      aria-label={
        target.kind === 'new'
          ? uiText('New page')
          : uiText('Editing {value1}', { value1: target.title })
      }
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="ws-page-editor-bar">
        <span className="ws-page-editor-badge">
          {target.kind === 'new' ? uiText('New page') : uiText('Editing')}
        </span>
        <span className="ws-page-editor-mode">
          {canPublish
            ? target.kind === 'new'
              ? uiText('Creating publishes the page under {value1} for everyone', {
                  value1: target.parentTitle,
                })
              : uiText('Saving publishes the new version for everyone')
            : uiText('Your changes are sent as a suggestion for a publisher')}
        </span>
        <span className="ws-page-editor-buttons">
          <button type="button" className="ws-button" disabled={busy} onClick={cancel}>
            {uiText('Cancel')}
          </button>
          <button
            type="button"
            className="ws-button primary"
            disabled={busy || !session}
            onClick={() => void save()}
          >
            {busy
              ? uiText('Saving…')
              : canPublish
                ? target.kind === 'new'
                  ? uiText('Create page')
                  : uiText('Save')
                : uiText('Send suggestion')}
          </button>
        </span>
      </div>
      {error && (
        <div className="ws-error ws-page-editor-error" role="alert">
          <p>{error}</p>
          {conflict && (
            <div className="ws-actions">
              <button type="button" className="ws-button" onClick={() => void copyDraft()}>
                {uiRich('{value1} Copy my text', {
                  value1: <WorkspaceIcon name="copy" size={14} />,
                })}
              </button>
              <button
                type="button"
                className="ws-button"
                onClick={() => {
                  reportUnsaved(false);
                  onCancel();
                }}
              >
                {uiText('Open the latest version')}
              </button>
            </div>
          )}
        </div>
      )}
      <div className="ws-page-editor-layout">
        <div className="ws-page-editor-main">
          <label className="ws-page-editor-title">
            <span className="ws-sr-only">{uiText('Page title')}</span>
            <input
              value={title}
              maxLength={200}
              placeholder={uiText('Page title')}
              onChange={(event) => setTitle(event.target.value)}
              onInput={(event) => setTitle((event.target as HTMLInputElement).value)}
            />
          </label>
          <Toolbar
            session={session}
            onPageLink={() => {
              const selection = session?.editor.state.selection;
              if (!selection) return;
              const text = selection.empty
                ? null
                : session!.editor.state.doc.textBetween(selection.from, selection.to, ' ').trim() ||
                  null;
              openPicker({ from: selection.from, to: selection.to }, text);
            }}
          />
          <div className="ws-page-editor-surface" ref={surface}>
            <EditorLoadBoundary onCancel={cancel}>
              <Suspense fallback={<LoadingState label={uiText('Loading the editor…')} />}>
                <LazyEditor
                  markdown={target.kind === 'page' ? target.markdown : ''}
                  label={
                    target.kind === 'new'
                      ? uiText('New page text')
                      : uiText('Text of {value1}', { value1: target.title })
                  }
                  placeholder={
                    target.kind === 'page' && !target.markdown
                      ? uiText('Write the page. Type [[ to link another page.')
                      : uiText('Type [[ to link another page.')
                  }
                  onSession={(next) => {
                    if (mounted.current) setSession(next);
                  }}
                  onPageLinkTrigger={({ range }) => openPicker(range, null)}
                />
              </Suspense>
            </EditorLoadBoundary>
            {picker && index && (
              <div
                ref={(node) => {
                  pickerPanel.current = node;
                  node?.scrollIntoView?.({ block: 'nearest' });
                }}
                className="ws-page-picker-popover"
                style={{ top: picker.top, left: picker.left }}
                role="dialog"
                aria-label={uiText('Link to a page')}
              >
                <div className="ws-page-picker-heading">{uiText('Link to a page')}</div>
                <PagePicker
                  index={index}
                  label={uiText('Link to a page')}
                  exclude={pageId ? new Set([pageId]) : undefined}
                  createUnder={createUnder}
                  busy={creating}
                  onChoose={(page) => insertLink(page)}
                  onCreate={canPublish ? (name) => void createAndLink(name) : undefined}
                  onClose={() => {
                    setPicker(null);
                    session?.editor.commands.focus();
                  }}
                />
              </div>
            )}
          </div>
          {notice.length > 0 && (
            <div className="ws-page-editor-notice" role="status">
              {notice.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          )}
        </div>
        <aside className="ws-page-editor-side">
          <label className="ws-page-field">
            <span>{reasonLabel}</span>
            <textarea
              value={note}
              rows={3}
              maxLength={canPublish ? 500 : 2000}
              required={!canPublish}
              placeholder={
                canPublish
                  ? uiText('What changed, in a few words')
                  : uiText('Where this comes from, in a few words')
              }
              onChange={(event) => setNote(event.target.value)}
              onInput={(event) => setNote((event.target as HTMLTextAreaElement).value)}
            />
            <small>
              {canPublish
                ? uiText('Shown in the page history.')
                : uiText('A publisher sees this next to your change.')}
            </small>
          </label>
          {openSuggestions > 0 && (
            <div className="ws-page-editor-hint">
              <strong>
                {openSuggestions === 1
                  ? uiText('1 suggestion is open on this page')
                  : uiText('{value1} suggestions are open on this page', {
                      value1: openSuggestions,
                    })}
              </strong>
              <p>
                {uiText(
                  'They stay open while you edit. After you save, they show against your new text; one whose words you changed is marked outdated.',
                )}
              </p>
            </div>
          )}
          <p className="ws-muted">
            {uiText(
              'Type [[ to link another page. Links keep working when a page is renamed or moved.',
            )}
          </p>
        </aside>
      </div>
    </form>
  );
}
